"use client";

import { c as _c } from "react/compiler-runtime";
import { Accordion } from "@base-ui/react/accordion";
import { ChevronDown, Gift, Heart, Info, Lightbulb, type LucideIcon, TrendingUp, Trophy, Users } from "lucide-react";
import { useState } from "react";
import { useLanguage } from "~/translations/loader/use-language";
interface EvpValue {
  accent: string;
  fg: string;
  icon: LucideIcon;
}
export const VALUES: EvpValue[] = [{
  icon: Trophy,
  accent: "#ea1b0a",
  fg: "#ffffff"
}, {
  icon: Lightbulb,
  accent: "#9d1207",
  fg: "#ffffff"
}, {
  icon: TrendingUp,
  accent: "#f05b48",
  fg: "#000000"
}, {
  icon: Users,
  accent: "#ca6fba",
  fg: "#000000"
}, {
  icon: Heart,
  accent: "#dba6d1",
  fg: "#000000"
}, {
  icon: Gift,
  accent: "#f6c1b0",
  fg: "#000000"
}];
export default function EvpEcon() {
  const $ = _c(26);
  const t = useLanguage();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [0];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [open, setOpen] = useState(t0);
  let t1;
  if ($[1] !== t.econ_evp_headline) {
    t1 = <h2 className="font-bold text-4xl text-black leading-tight xl:text-5xl">{t.econ_evp_headline}</h2>;
    $[1] = t.econ_evp_headline;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t.econ_evp_subheadline) {
    t2 = <p className="mt-4 font-bold text-2xl text-primary xl:text-3xl">{t.econ_evp_subheadline}</p>;
    $[3] = t.econ_evp_subheadline;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t.econ_evp_intro) {
    t3 = <p className="mt-4 text-black text-lg leading-relaxed">{t.econ_evp_intro}</p>;
    $[5] = t.econ_evp_intro;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <div className="text-left lg:sticky lg:top-36 lg:mt-12">{t1}{t2}{t3}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== open || $[12] !== t) {
    t5 = VALUES.map((value, i) => {
      const isOpen = open[0] === i;
      const Icon = value.icon;
      return <Accordion.Item key={value.accent} value={i}><div className="relative -mb-1 transition-transform duration-100 ease-out active:scale-[0.99] motion-reduce:transition-none"><HexBar fill={isOpen ? `${value.accent}1f` : "#ffffff"} stroke={value.accent} strokeWidth={isOpen ? 2.6 : 1.6} /><Accordion.Header><Accordion.Trigger className="group relative flex w-full cursor-pointer items-center gap-3 px-[6%] py-4 text-left focus-visible:outline-2 focus-visible:outline-black focus-visible:outline-offset-2 lg:gap-4 lg:py-5"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-bold text-xs" style={{
                background: value.accent,
                color: value.fg
              }}>{String(i + 1).padStart(2, "0")}</span><Icon aria-hidden="true" className="shrink-0" color={value.accent} size={26} strokeWidth={1.5} /><span className="flex-1 font-bold text-base text-black leading-tight lg:text-lg">{t[`econ_evp_title${i + 1}` as keyof typeof t]}</span><ChevronDown aria-hidden="true" className="shrink-0 transition-transform duration-200 ease-out group-data-[panel-open]:rotate-180 motion-reduce:transition-none" color={value.accent} size={18} strokeWidth={2} /></Accordion.Trigger></Accordion.Header></div><Accordion.Panel className="h-(--accordion-panel-height) overflow-hidden transition-[height] duration-200 ease-out data-[ending-style]:h-0 data-[starting-style]:h-0 motion-reduce:transition-none" style={{
          background: `linear-gradient(180deg, ${value.accent}26 0%, ${value.accent}00 100%)`,
          marginInline: "4%"
        }}><p className="px-5 pt-4 pb-5 text-base text-black leading-relaxed lg:px-6 lg:pt-5 lg:pb-6 lg:text-lg">{t[`econ_evp_text${i + 1}` as keyof typeof t]}</p></Accordion.Panel></Accordion.Item>;
    });
    $[11] = open;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== open || $[15] !== t5) {
    t6 = <Accordion.Root className="space-y-4" onValueChange={setOpen} value={open}>{t5}</Accordion.Root>;
    $[14] = open;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Info aria-hidden="true" className="text-primary" size={16} />;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t.econ_evp_hint) {
    t8 = <p className="mt-6 flex items-center gap-2 text-slate-500 text-xs">{t7}{t.econ_evp_hint}</p>;
    $[18] = t.econ_evp_hint;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t6 || $[21] !== t8) {
    t9 = <div>{t6}{t8}</div>;
    $[20] = t6;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t4 || $[24] !== t9) {
    t10 = <section className="outer-container py-container print:hidden" id="benefits"><div className="mx-auto w-full max-w-380"><div className="grid gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:items-start lg:gap-16">{t4}{t9}</div></div></section>;
    $[23] = t4;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}
const HEX_PATH = "M 21.3 0.8 L 378.7 0.8 A 9.0 9.0 0 0 1 386.7 5.7 L 397.1 25.9 A 9.0 9.0 0 0 1 397.1 34.1 L 386.7 54.3 A 9.0 9.0 0 0 1 378.7 59.2 L 21.3 59.2 A 9.0 9.0 0 0 1 13.3 54.3 L 2.9 34.1 A 9.0 9.0 0 0 1 2.9 25.9 L 13.3 5.7 A 9.0 9.0 0 0 1 21.3 0.8 Z";
function HexBar(t0) {
  const $ = _c(4);
  const {
    fill,
    stroke,
    strokeWidth
  } = t0;
  let t1;
  if ($[0] !== fill || $[1] !== stroke || $[2] !== strokeWidth) {
    t1 = <svg aria-hidden="true" className="filter-[drop-shadow(0_4px_10px_rgb(0_0_0/0.08))] absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 400 60"><path d={HEX_PATH} fill={fill} stroke={stroke} strokeLinejoin="round" strokeWidth={strokeWidth} vectorEffect="non-scaling-stroke" /></svg>;
    $[0] = fill;
    $[1] = stroke;
    $[2] = strokeWidth;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}